import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { athletesAPI, resultsAPI } from '../api';
import { useTranslation } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { toast } from "../components/common/Notifier";


export const OnboardingPage = () => {
  const { t } = useTranslation();
  const { checkAuth, athlete } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [weeklyVolume, setWeeklyVolume] = useState('');
  const [testDistance, setTestDistance] = useState('');
  const [testMin, setTestMin] = useState('');
  const [testSec, setTestSec] = useState('');
  const [resultMessage, setResultMessage] = useState(null);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);



  const handleGoToDashboard = () => {
    toast.success(t('common.success'));
    navigate('/dashboard');
  };

  const handleFinish = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await athletesAPI.updateMe({
        name,
        email,
        weekly_volume_km: parseFloat(weeklyVolume),
      });

      const { data } = await resultsAPI.create({
        athlete: athlete.id,
        distance_m: parseFloat(testDistance),
        time_min: parseInt(testMin, 10),
        time_sec: parseInt(testSec, 10),
      });

      setResultMessage({ vdot: data.calculated_vdot, commentKey: data.vdot_comment_key });
      await checkAuth();
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-gradient-to-b from-blue-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n}
                 className={`h-2 w-12 rounded-full transition-all ${n <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
          ))}
        </div>

        <form onSubmit={step === 3 ? handleFinish : (e) => { e.preventDefault(); nextStep(); }}>
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">{t('onboarding.step1_title')}</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('login.username')}
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder={t('onboarding.name_placeholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  ✉️ {t('register.email')}
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder={t('register.email_placeholder')}
                  autoComplete="email"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 px-4 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all"
              >
                {t('onboarding.next')}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">{t('onboarding.step2_title')}</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('onboarding.weekly_volume')}
                </label>
                <input
                  type="number"
                  required
                  value={weeklyVolume}
                  onChange={(e) => setWeeklyVolume(e.target.value)}
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="40"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
                >
                  {t('onboarding.back')}
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 px-4 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all"
                >
                  {t('onboarding.next')}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800">{t('onboarding.step3_title')}</h2>
            <p className="text-sm text-gray-500">{t('onboarding.step3_desc')}</p>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('onboarding.test_distance')}
              </label>
              <input
                type="number"
                required
                value={testDistance}
                onChange={(e) => setTestDistance(e.target.value)}
                className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                placeholder="5000"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('onboarding.minutes')}
                </label>
                <input
                  type="number"
                  required
                  value={testMin}
                  onChange={(e) => setTestMin(e.target.value)}
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('onboarding.seconds')}
                </label>
                <input
                  type="number"
                  required
                  value={testSec}
                  onChange={(e) => setTestSec(e.target.value)}
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
              >
                {t('onboarding.back')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
              >
                {loading ? t('common.loading') : t('onboarding.finish')}
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 text-center">
            <h2 className="text-xl font-bold text-gray-800">{t('onboarding.result_title')}</h2>
            <p className="text-3xl font-bold text-blue-600">VDOT: {resultMessage?.vdot}</p>
            {resultMessage?.commentKey && (
              <p className="text-sm text-gray-600">{t(resultMessage.commentKey)}</p>
            )}
            <button
              type="button"
              onClick={handleGoToDashboard}
              className="w-full py-3 px-4 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              {t('onboarding.go_to_dashboard')}
            </button>
          </div>
        )}



        </form>
      </div>
    </div>
  );
};